import React from 'react';
import { isKlaziaInternationalSchool } from 'utils/studentSchools';
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Select,
  Checkbox,
  CheckboxGroup,
  RadioGroup,
  Radio,
  Stack,
  Box,
  Text,
  Spinner,
  Input,
} from '@chakra-ui/react';
import qs from 'qs';
import xhr from 'xhr';

// Rango de grados por tipo de transcript.
const TYPE_RANGES = {
  elementary: [1, 5],
  middle: [6, 8],
  high: [9, 12],
};

const ORDINALS = {
  primero: 1, segundo: 2, tercero: 3, cuarto: 4, quinto: 5, sexto: 6,
  septimo: 7, octavo: 8, noveno: 9, decimo: 10, undecimo: 11, once: 11,
  duodecimo: 12, doce: 12,
};

const normalize = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

const gradeNumberFromName = (name) => {
  const n = normalize(name);
  // Del más largo al más corto: "undecimo"/"duodecimo" contienen "decimo".
  const keys = Object.keys(ORDINALS).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (n.includes(k)) return ORDINALS[k];
  }
  return null;
};

// program_type de modalidad USA (regla A, razón 1).
const isUsaProgramType = (ptName) => normalize(ptName).includes('usa');

const yearOf = (semester) => {
  if (semester?.start_date) return new Date(semester.start_date).getFullYear();
  return semester?.name || '';
};

function TranscriptModal({ student, idEst }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [loading, setLoading] = React.useState(true);
  const [enrollments, setEnrollments] = React.useState([]); // grade-enrollments con grade/program_type/semester
  const [type, setType] = React.useState('elementary');
  const [selected, setSelected] = React.useState([]); // ids (string) marcados
  const [gender, setGender] = React.useState('');
  const [graduationDate, setGraduationDate] = React.useState(''); // YYYY-MM-DD, opcional
  const [generating, setGenerating] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const query = qs.stringify(
          {
            filters: { student: { id: { $eq: idEst } } },
            populate: { grade: { populate: ['program_type'] }, semester: true },
            pagination: { limit: -1 },
          },
          { encodeValuesOnly: true }
        );
        const res = await xhr(`/grade-enrollments?${query}`);
        const list = (res?.data?.data || []).map((e) => ({
          id: e.id,
          gradeName: e.grade?.name || '',
          gradeNumber: gradeNumberFromName(e.grade?.name),
          programType: e.grade?.program_type?.name || '',
          year: yearOf(e.semester),
        }));
        if (!cancelled) setEnrollments(list);
      } catch (e) {
        if (!cancelled) setEnrollments([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [idEst]);

  // Regla A: school = klazia international school O alguna matrícula USA.
  const qualifies =
    isKlaziaInternationalSchool(student?.school) ||
    enrollments.some((e) => isUsaProgramType(e.programType));

  // No renderizar nada mientras carga o si no aplica.
  if (loading) return null;
  if (!qualifies) return null;

  const [min, max] = TYPE_RANGES[type] || [0, 0];
  const gradesInRange = enrollments
    .filter((e) => e.gradeNumber && e.gradeNumber >= min && e.gradeNumber <= max)
    .sort((a, b) => a.gradeNumber - b.gradeNumber);

  const handleGenerate = async () => {
    if (!selected.length) {
      toast({ title: 'Selecciona al menos un grado', status: 'warning', duration: 3000 });
      return;
    }
    if (!gender) {
      toast({ title: 'Selecciona el gender', status: 'warning', duration: 3000 });
      return;
    }
    try {
      setGenerating(true);
      const res = await xhr.post(
        `/students/${idEst}/transcript`,
        {
          type,
          gradeEnrollmentIds: selected.map((x) => Number(x)),
          gender,
          graduationDate: graduationDate || undefined,
        },
        { responseType: 'blob' }
      );
      const href = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `transcript-${type}-${idEst}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
      toast({ title: 'Transcript generado', status: 'success', duration: 4000 });
    } catch (err) {
      // La respuesta viene como blob (responseType: 'blob'); si es un error del
      // backend (ej. falta la insignia), leemos el blob para mostrar el mensaje.
      let description = 'Revisa la consola o inténtalo de nuevo.';
      try {
        const body = err?.response?.data;
        if (body instanceof Blob) {
          const txt = await body.text();
          const j = JSON.parse(txt);
          description = j?.error?.message || j?.message || description;
        }
      } catch (_) {
        // si no se puede leer, queda el mensaje genérico
      }
      toast({
        title: 'No se pudo generar el transcript',
        description,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Button colorScheme="teal" size="xs" mt="10px" ml="10px" onClick={onOpen}>
        Transcript (US)
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generar Transcript (US)</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb="14px">
              <Text fontWeight="600" fontSize="sm" mb="4px">
                Tipo de transcript
              </Text>
              <Select
                size="sm"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setSelected([]);
                }}
              >
                <option value="elementary">Elementary (grados 1–5)</option>
                <option value="middle">Middle (grados 6–8)</option>
                <option value="high">High (grados 9–12)</option>
              </Select>
            </Box>

            <Box mb="14px">
              <Text fontWeight="600" fontSize="sm" mb="4px">
                Grados a incluir (marca los de modalidad USA)
              </Text>
              {gradesInRange.length === 0 ? (
                <Text fontSize="sm" color="gray.500">
                  El estudiante no tiene matrículas en el rango de este transcript.
                </Text>
              ) : (
                <CheckboxGroup value={selected} onChange={(val) => setSelected(val)}>
                  <Stack spacing="6px">
                    {gradesInRange.map((e) => (
                      <Checkbox key={e.id} value={String(e.id)}>
                        {e.gradeName}
                        {e.programType ? ` — ${e.programType}` : ''}
                        {e.year ? ` — ${e.year}` : ''}
                      </Checkbox>
                    ))}
                  </Stack>
                </CheckboxGroup>
              )}
            </Box>

            <Box mb="14px">
              <Text fontWeight="600" fontSize="sm" mb="4px">
                Gender
              </Text>
              <RadioGroup value={gender} onChange={setGender}>
                <Stack direction="row" spacing="20px">
                  <Radio value="Male">Male</Radio>
                  <Radio value="Female">Female</Radio>
                </Stack>
              </RadioGroup>
            </Box>

            <Box mb="4px">
              <Text fontWeight="600" fontSize="sm" mb="4px">
                Graduation date (opcional)
              </Text>
              <Input
                size="sm"
                type="date"
                value={graduationDate}
                onChange={(e) => setGraduationDate(e.target.value)}
              />
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} size="sm">
              Cancelar
            </Button>
            <Button
              colorScheme="teal"
              size="sm"
              onClick={handleGenerate}
              isLoading={generating}
              isDisabled={generating || gradesInRange.length === 0}
            >
              Descargar transcript
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default TranscriptModal;
